(function () {
  'use strict';

  var registry = new WeakMap();

  function init(root) {
    if (!root || registry.has(root)) return;

    var controller = new AbortController();
    var signal = controller.signal;
    var reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    var reduceMotion = reduceMotionQuery.matches;
    var hasGsap = Boolean(window.gsap);
    var hasScrollTrigger = hasGsap && Boolean(window.ScrollTrigger);
    // Entrance animations start their targets at visibility:hidden and reveal
    // them as GSAP ticks. The theme editor re-renders a section after most
    // edits, and a background tab never ticks, so in the editor the hero and
    // the chapters sat invisible and could not be outlined or clicked. Skip the
    // entrances there (the landing section does the same); the scroll-scrubbed
    // parallax stays, it never hides anything.
    var designMode = Boolean(window.Shopify && window.Shopify.designMode);
    var entrance = hasGsap && !reduceMotion && !designMode;
    var motionContext = null;
    // The hero parallax scrubs an inline transform onto the gallery images, which
    // outranks any stylesheet rule. The zoom has to be able to stand it down.
    var galleryParallax = null;

    document.documentElement.classList.add('pg-flow-ready');

    if (hasScrollTrigger) window.gsap.registerPlugin(window.ScrollTrigger);

    function initCollection() {
      if (!root.matches('[data-pgc-root]')) return;

      var intro = root.querySelector('.pgc-intro');
      var products = Array.from(root.querySelectorAll('[data-pgc-product]'));
      var newsletter = root.querySelector('[data-pgc-newsletter]');
      var footer = root.querySelector('.pgc-footer');

      if (hasGsap && !reduceMotion) {
        var introItems = intro && entrance ? [intro.querySelector('.pgc-kicker'), intro.querySelector('h1'), intro.querySelector('.pgc-intro__copy')].filter(Boolean) : [];
        if (introItems.length) {
          window.gsap.timeline({ defaults: { ease: 'power4.out' } })
            .from(introItems[0], { autoAlpha: 0, duration: .42, y: 10 })
            .from(introItems.slice(1), { autoAlpha: 0, duration: .68, stagger: .07, y: 24 }, .07);
        }

        if (hasScrollTrigger) {
          products.forEach(function (product, index) {
            var mediaNode = product.querySelector('.pgc-product__media');
            var mediaImage = mediaNode?.querySelector('img');
            var textNodes = [product.querySelector('.pgc-product__meta'), product.querySelector('.pgc-product__details')].filter(Boolean);
            var timeline = entrance ? window.gsap.timeline({
              scrollTrigger: { once: true, start: 'top 88%', trigger: product }
            }) : null;
            if (timeline && mediaNode) {
              timeline.from(mediaNode, { autoAlpha: 0, duration: index === 0 ? .72 : .52, ease: 'power4.out', scale: index === 0 ? .985 : .99, y: index === 0 ? 20 : 12 });
            }
            if (timeline && textNodes.length) {
              timeline.from(textNodes, { autoAlpha: 0, duration: .4, ease: 'power3.out', stagger: .06, y: 10 }, .1);
            }

            if (mediaImage) {
              window.gsap.fromTo(mediaImage, { scale: 1.015, yPercent: -1.5 }, {
                ease: 'none',
                scale: 1.035,
                scrollTrigger: { end: 'bottom top', scrub: .4, start: 'top bottom', trigger: product },
                yPercent: 1.5
              });
            }
          });

          if (entrance) [newsletter, footer].filter(Boolean).forEach(function (element) {
            window.gsap.from(element, {
              autoAlpha: 0,
              duration: .72,
              ease: 'power4.out',
              scrollTrigger: { once: true, start: 'top 90%', trigger: element },
              y: 18
            });
          });
        }
      }

    }

    // Click a gallery image to zoom into it, then the cursor drives what you are
    // looking at. Pointer-fine only: on touch there is no hover to pan with, and
    // hijacking tap would break swiping between panes.
    //
    // The pan is transform-origin, not a transition — origin changes are not
    // animated, so the image tracks the cursor 1:1 while only the scale itself
    // eases on the way in and out. The stage already has overflow:hidden, so it
    // is the viewport for free.
    function initImageZoom() {
      if (!root.classList.contains('pg-pdp')) return;

      var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
      var zoomedImage = null;
      var origin = { x: 50, y: 50 };

      // The parallax owns these images' inline transform and rewrites it on every
      // scroll tick, so it has to stand down for the duration of the zoom or it
      // simply overwrites it. disable(false) leaves the current value in place
      // rather than reverting, so nothing jumps on the way in.
      function suspendParallax(suspend) {
        var trigger = galleryParallax && galleryParallax.scrollTrigger;
        if (!trigger) return;
        if (suspend) trigger.disable(false);
        else trigger.enable();
      }

      function clearZoom() {
        if (!zoomedImage) return;
        var image = zoomedImage;
        zoomedImage = null;
        image.classList.remove('is-zoomed');
        if (hasGsap) {
          window.gsap.to(image, {
            duration: .3,
            ease: 'power3.out',
            scale: 1,
            onComplete: function () {
              window.gsap.set(image, { clearProps: 'transform,transformOrigin,willChange' });
              suspendParallax(false);
            }
          });
        } else {
          image.style.removeProperty('--pg-zoom-x');
          image.style.removeProperty('--pg-zoom-y');
          suspendParallax(false);
        }
      }

      function trackCursor(event, image) {
        var rect = image.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        origin.x = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100));
        origin.y = Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100));
        if (hasGsap) {
          // set(), not to(): the pan must track the cursor 1:1, not chase it.
          window.gsap.set(image, { transformOrigin: origin.x + '% ' + origin.y + '%' });
        } else {
          image.style.setProperty('--pg-zoom-x', origin.x + '%');
          image.style.setProperty('--pg-zoom-y', origin.y + '%');
        }
      }

      Array.from(root.querySelectorAll('.pg-pdp__main-image')).forEach(function (image) {
        var stage = image.closest('[data-pg-media-pane]') || image.parentElement;
        if (!stage) return;

        image.addEventListener('click', function (event) {
          if (!finePointer.matches) return;
          event.preventDefault();
          if (zoomedImage === image) {
            clearZoom();
            return;
          }
          clearZoom();
          zoomedImage = image;
          suspendParallax(true);
          trackCursor(event, image);
          image.classList.add('is-zoomed');
          if (hasGsap) {
            window.gsap.set(image, { willChange: 'transform' });
            // Scale only. transformOrigin is deliberately not in this tween:
            // an active tween owns the properties it animates and rewrites them
            // every frame, so including it here made the image ignore the cursor
            // until the 340ms had elapsed. trackCursor keeps setting the origin
            // directly, which composes with the running scale tween, so the
            // preview pans from the first mousemove.
            window.gsap.to(image, {
              duration: .34,
              ease: 'power3.out',
              scale: 2.4
            });
          }
        }, { signal: signal });

        stage.addEventListener('mousemove', function (event) {
          if (zoomedImage !== image) return;
          trackCursor(event, image);
        }, { signal: signal });

        // Leaving the stage while zoomed would otherwise strand it magnified on
        // whatever corner the cursor exited through.
        stage.addEventListener('mouseleave', function () {
          if (zoomedImage === image) clearZoom();
        }, { signal: signal });
      });

      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') clearZoom();
      }, { signal: signal });
    }

    function initPdp() {
      if (!root.classList.contains('pg-pdp')) return;

      var panes = Array.from(root.querySelectorAll('[data-pg-media-pane]'));
      var thumbs = Array.from(root.querySelectorAll('[data-pg-thumb-target]'));
      var counter = root.querySelector('[data-pg-media-count]');
      var galleryCaption = root.querySelector('[data-pg-gallery-caption]');
      var galleryNumber = root.querySelector('[data-pg-gallery-number]');
      var galleryTitle = root.querySelector('[data-pg-gallery-title]');
      var subnav = Array.from(root.querySelectorAll('.pg-pdp__subnav a'));
      var editionLabel = root.querySelector('[data-pg-edition-label]');
      var editionGlyphs = [];
      var switchToken = 0;
      var galleryTimeline = null;
      var activeIndex = Math.max(0, panes.findIndex(function (pane) { return !pane.hidden; }));
      if (editionLabel) {
        var editionText = editionLabel.textContent.trim();
        var editionFragment = document.createDocumentFragment();
        editionLabel.setAttribute('aria-label', editionText);

        Array.from(editionText).forEach(function (character) {
          var letterMask = document.createElement('span');
          var glyph = document.createElement('span');
          letterMask.className = 'pg-pdp__edition-letter';
          letterMask.setAttribute('aria-hidden', 'true');
          glyph.className = 'pg-pdp__edition-glyph';
          glyph.textContent = character === ' ' ? '\u00a0' : character;
          letterMask.appendChild(glyph);
          editionFragment.appendChild(letterMask);
          editionGlyphs.push(glyph);
        });

        editionLabel.textContent = '';
        editionLabel.appendChild(editionFragment);
        editionLabel.classList.add('is-split');
      }

      function captionFor(index) {
        return (panes[index]?.dataset.pgCaption || '').trim();
      }

      function updateCaption(index) {
        var number = String(index + 1).padStart(2, '0');
        var title = captionFor(index);
        if (galleryNumber) galleryNumber.textContent = number;
        if (galleryTitle) galleryTitle.textContent = title;
        if (galleryCaption) {
          // Same pattern the section renders server-side (PEGASI product -> Screen reader labels).
          var pattern = galleryCaption.dataset.pgAriaPattern || 'Image [n] of [total], [caption]';
          galleryCaption.setAttribute('aria-label', pattern.replace('[n]', String(index + 1)).replace('[total]', String(panes.length)).replace('[caption]', title.toLowerCase()));
        }
      }

      function syncGalleryControls(index) {
        thumbs.forEach(function (thumb, thumbIndex) {
          var selected = thumbIndex === index;
          thumb.classList.toggle('is-active', selected);
          thumb.setAttribute('aria-selected', String(selected));
          thumb.tabIndex = selected ? 0 : -1;
        });
        if (counter) counter.textContent = String(index + 1).padStart(2, '0') + ' / ' + String(panes.length).padStart(2, '0');
      }

      function normalizeGallery(targetIndex, reset) {
        if (!hasGsap) return activeIndex;
        if (galleryTimeline) {
          galleryTimeline.kill();
          galleryTimeline = null;
        }
        var captionNodes = [galleryNumber, galleryTitle].filter(Boolean);
        window.gsap.killTweensOf(panes.concat(captionNodes));
        if (reset) {
          window.gsap.set(panes.concat(captionNodes), { clearProps: 'opacity,transform,visibility' });
          panes.forEach(function (pane, paneIndex) { pane.hidden = paneIndex !== activeIndex; });
          return activeIndex;
        }

        var visibleIndexes = panes.reduce(function (indexes, pane, paneIndex) {
          if (!pane.hidden) indexes.push(paneIndex);
          return indexes;
        }, []);
        var outgoingIndex = visibleIndexes.reduce(function (bestIndex, paneIndex) {
          if (bestIndex < 0) return paneIndex;
          var bestOpacity = Number.parseFloat(window.getComputedStyle(panes[bestIndex]).opacity) || 0;
          var paneOpacity = Number.parseFloat(window.getComputedStyle(panes[paneIndex]).opacity) || 0;
          return paneOpacity > bestOpacity ? paneIndex : bestIndex;
        }, -1);
        if (outgoingIndex < 0) outgoingIndex = activeIndex;

        panes.forEach(function (pane, paneIndex) {
          if (paneIndex === outgoingIndex || paneIndex === targetIndex) return;
          pane.hidden = true;
          window.gsap.set(pane, { clearProps: 'opacity,transform,visibility' });
        });
        return outgoingIndex;
      }

      function setCurrent(index, options) {
        if (!panes[index] || !thumbs[index]) return;
        var immediate = Boolean(options && options.immediate) || reduceMotion || !hasGsap;
        var currentIndex = normalizeGallery(index, false);
        var current = panes[currentIndex];
        var next = panes[index];
        var direction = index >= currentIndex ? 1 : -1;
        var token = ++switchToken;

        syncGalleryControls(index);
        if (current === next) {
          activeIndex = index;
          panes.forEach(function (pane, paneIndex) { pane.hidden = paneIndex !== index; });
          if (hasGsap) window.gsap.set(panes, { clearProps: 'opacity,transform,visibility' });
          updateCaption(index);
          return;
        }
        var currentModel = current?.querySelector('product-model');
        if (currentModel && typeof currentModel.pauseMedia === 'function') currentModel.pauseMedia();

        activeIndex = index;
        if (immediate || !current) {
          if (hasGsap) window.gsap.set(panes.concat([galleryNumber, galleryTitle].filter(Boolean)), { clearProps: 'opacity,transform,visibility' });
          panes.forEach(function (pane, paneIndex) { pane.hidden = paneIndex !== index; });
          updateCaption(index);
          return;
        }

        var captionNodes = [galleryNumber, galleryTitle].filter(Boolean);
        var nextWasHidden = next.hidden;
        current.hidden = false;
        if (nextWasHidden) {
          next.hidden = false;
          window.gsap.set(next, { autoAlpha: 0, scale: .992, xPercent: direction * .8 });
        } else {
          next.hidden = false;
        }

        galleryTimeline = window.gsap.timeline({
          defaults: { overwrite: 'auto' },
          onComplete: function () {
            if (token !== switchToken) return;
            panes.forEach(function (pane, paneIndex) { pane.hidden = paneIndex !== activeIndex; });
            window.gsap.set(panes.concat(captionNodes), { clearProps: 'opacity,transform,visibility' });
            galleryTimeline = null;
          }
        });
        galleryTimeline
          .to(current, {
            autoAlpha: 0,
            duration: .18,
            ease: 'power2.out',
            scale: .992,
            xPercent: direction * -.8
          }, 0)
          .to(next, {
            autoAlpha: 1,
            duration: .26,
            ease: 'power4.out',
            scale: 1,
            xPercent: 0
          }, .04);

        if (captionNodes.length) {
          updateCaption(index);
          galleryTimeline.fromTo(captionNodes,
            { autoAlpha: 0, yPercent: direction > 0 ? 115 : -115 },
            {
              autoAlpha: 1,
              duration: .22,
              ease: 'power4.out',
              immediateRender: false,
              stagger: .025,
              yPercent: 0
            }, 0);
        } else {
          updateCaption(index);
        }
      }

      thumbs.forEach(function (thumb, index) {
        thumb.addEventListener('click', function (event) {
          setCurrent(index, { immediate: event.detail === 0 });
        }, { signal: signal });
        thumb.addEventListener('keydown', function (event) {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setCurrent(index, { immediate: true });
            return;
          }
          var target = index;
          if (event.key === 'ArrowRight') target = (index + 1) % thumbs.length;
          else if (event.key === 'ArrowLeft') target = (index - 1 + thumbs.length) % thumbs.length;
          else if (event.key === 'Home') target = 0;
          else if (event.key === 'End') target = thumbs.length - 1;
          else return;
          event.preventDefault();
          setCurrent(target, { immediate: true });
          thumbs[target].focus();
        }, { signal: signal });
      });

      syncGalleryControls(activeIndex);
      updateCaption(activeIndex);
      controller.signal.addEventListener('abort', function () {
        switchToken += 1;
        normalizeGallery(activeIndex, true);
      }, { once: true });

      var modelFallback = root.querySelector('[data-pg-model-fallback]');
      if (modelFallback && hasGsap && !reduceMotion && window.matchMedia('(pointer: fine)').matches) {
        var poster = modelFallback.querySelector('.pg-pdp__model-poster');
        var rotateX = window.gsap.quickTo(poster, 'rotationX', { duration: .18, ease: 'power3.out' });
        var rotateY = window.gsap.quickTo(poster, 'rotationY', { duration: .18, ease: 'power3.out' });
        modelFallback.addEventListener('pointermove', function (event) {
          var bounds = modelFallback.getBoundingClientRect();
          rotateY(((event.clientX - bounds.left) / bounds.width - .5) * 6);
          rotateX(-((event.clientY - bounds.top) / bounds.height - .5) * 6);
        }, { signal: signal });
        modelFallback.addEventListener('pointerleave', function () {
          rotateX(0);
          rotateY(0);
        }, { signal: signal });
        signal.addEventListener('abort', function () {
          rotateX.tween?.kill();
          rotateY.tween?.kill();
          window.gsap.set(poster, { clearProps: 'transform' });
        }, { once: true });
      }

      if (hasGsap && !reduceMotion) {
        var heroPane = root.querySelector('.pg-pdp__media-stage:not([hidden])');
        var heroImage = heroPane?.querySelector('.pg-pdp__main-image, .pg-pdp__model-poster');
        var galleryImages = panes.map(function (pane) {
          return pane.querySelector('.pg-pdp__main-image, .pg-pdp__model-poster');
        }).filter(Boolean);
        var heroCopy = [
          root.querySelector('.pg-pdp__availability'),
          root.querySelector('.pg-pdp__series'),
          root.querySelector('.pg-pdp__title'),
          root.querySelector('.pg-pdp__tagline'),
          root.querySelector('.pg-pdp__description'),
          root.querySelector('.pg-pdp__price-row'),
          root.querySelector('.pg-pdp__form'),
          root.querySelector('.pg-pdp__notes')
        ].filter(Boolean);

        var heroIntro = entrance ? window.gsap.timeline({ defaults: { ease: 'power4.out' } }) : null;
        if (heroIntro && heroImage) {
          heroIntro.from(heroImage, { autoAlpha: 0, duration: .86, scale: .965, y: 24 }, 0);
        }
        if (heroIntro) heroIntro.from(heroCopy, { autoAlpha: 0, duration: .58, stagger: .055, y: 24 }, .08);

        if (heroIntro && editionGlyphs.length) {
          window.gsap.set(editionGlyphs, { willChange: 'transform' });
          heroIntro.fromTo(editionGlyphs,
            { yPercent: 135 },
            {
              clearProps: 'transform,willChange',
              duration: .64,
              ease: 'power4.out',
              stagger: .03,
              yPercent: 0
            }, .38);
        }

        if (hasScrollTrigger) {
          var hero = root.querySelector('.pg-pdp__hero');
          var details = root.querySelector('.pg-pdp__details');
          if (hero && heroImage && details) {
            galleryParallax = window.gsap.timeline({
              scrollTrigger: { end: 'bottom top', scrub: .4, start: 'top top', trigger: hero }
            })
              .to(galleryImages, { ease: 'none', scale: 1.035, yPercent: 3 }, 0)
              .to(details, { autoAlpha: .42, ease: 'none', y: -34 }, 0);
          }

          [
            ['.pg-pdp__overview-media img', '.pg-pdp__overview'],
            ['.pg-pdp__construction-media img', '.pg-pdp__construction'],
            ['.pg-pdp__edition-image img', '.pg-pdp__edition']
          ].forEach(function (pair) {
            var image = root.querySelector(pair[0]);
            var section = root.querySelector(pair[1]);
            if (!image || !section) return;
            // The image is sized to exactly fill a container with overflow:hidden,
            // so translating it uncovers the container edge and the dark page
            // behind shows as a band — most visible scrubbing slowly, which parks
            // the tween mid-travel instead of passing through it. Scale is held
            // constant (not animated) at more than the 4% peak travel so the image
            // always overfills: 1.1 gives 5% of overhang per edge against 4% of shift.
            window.gsap.fromTo(image, { scale: 1.1, yPercent: -3 }, {
              ease: 'none',
              scale: 1.1,
              scrollTrigger: {
                end: 'bottom top',
                onToggle: function (state) { image.style.willChange = state.isActive ? 'transform' : 'auto'; },
                scrub: .35,
                start: 'top bottom',
                trigger: section
              },
              yPercent: 4
            });
            controller.signal.addEventListener('abort', function () { image.style.willChange = ''; }, { once: true });
          });

          // These blocks used gsap.from({autoAlpha: 0}) inside onEnter. A .from()
          // tween applies its start values at the moment it is CREATED, and the
          // targets had no hidden state before that, so each block painted fully
          // visible, snapped to invisible when the trigger crossed, then faded
          // back in. That read as blinking on the way down the page.
          //
          // Pre-set the hidden state up front and animate .to() the natural
          // state instead, so an element is never seen before its reveal.
          var batchSelector = '.pg-pdp .pg-metric, .pg-pdp .pg-build-list > div, .pg-pdp .pg-spec-group, .pg-pdp .pg-spec-box, .pg-pdp .pg-review-score, .pg-pdp .pg-review-empty';
          var batchTargets = Array.prototype.slice.call(root.querySelectorAll(batchSelector));

          if (batchTargets.length && entrance) {
            window.gsap.set(batchTargets, { autoAlpha: 0, y: 20 });

            window.ScrollTrigger.batch(batchSelector, {
              batchMax: 5,
              interval: .08,
              once: true,
              onEnter: function (elements) {
                window.gsap.to(elements, {
                  autoAlpha: 1,
                  duration: .68,
                  ease: 'power4.out',
                  stagger: .06,
                  y: 0,
                  // Drop the inline transform and visibility once landed so the
                  // text inside returns to normal, non-composited painting.
                  clearProps: 'transform,opacity,visibility'
                });
              },
              start: 'top 91%'
            });

            // Anything still hidden if this controller tears down must not be
            // left invisible.
            controller.signal.addEventListener('abort', function () {
              window.gsap.set(batchTargets, { clearProps: 'opacity,transform,visibility' });
            }, { once: true });
          }

          subnav.forEach(function (link) {
            var target = root.querySelector(link.getAttribute('href'));
            if (!target) return;
            window.ScrollTrigger.create({
              end: 'bottom 38%',
              onToggle: function (state) {
                if (!state.isActive) return;
                subnav.forEach(function (item) { item.removeAttribute('aria-current'); });
                link.setAttribute('aria-current', 'location');
              },
              start: 'top 38%',
              trigger: target
            });
          });
        }
      }
    }

    if (hasGsap) {
      motionContext = window.gsap.context(function () {
        initCollection();
        initPdp();
        initImageZoom();
      }, root);
    } else {
      initCollection();
      initPdp();
      initImageZoom();
    }

    if (hasScrollTrigger) {
      var refresh = window.gsap.delayedCall(.08, function () { window.ScrollTrigger.refresh(); });
      document.fonts?.ready.then(function () { refresh.restart(true); });
      root.querySelectorAll('img').forEach(function (image) {
        if (!image.complete) image.addEventListener('load', function () { refresh.restart(true); }, { once: true, signal: signal });
      });
      controller.signal.addEventListener('abort', function () { refresh.kill(); }, { once: true });
    }

    registry.set(root, { controller: controller, motionContext: motionContext });

    function refreshMotionPreference() {
      destroy(root);
      window.requestAnimationFrame(function () {
        if (root.isConnected) init(root);
      });
    }

    if (reduceMotionQuery.addEventListener) {
      reduceMotionQuery.addEventListener('change', refreshMotionPreference, { signal: signal });
    } else if (reduceMotionQuery.addListener) {
      reduceMotionQuery.addListener(refreshMotionPreference);
      signal.addEventListener('abort', function () {
        reduceMotionQuery.removeListener(refreshMotionPreference);
      }, { once: true });
    }
  }

  function destroy(root) {
    var entry = registry.get(root);
    if (!entry) return;
    entry.controller.abort();
    entry.motionContext?.revert();
    registry.delete(root);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-pgc-root], .pg-pdp').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); }, { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('[data-pgc-root], .pg-pdp').forEach(destroy);
  });
}());
